import jwtDecode from 'jwt-decode';
import { handle } from 'redux-pack';
import { Action } from "../../../types/actions";
import { BlProfile, BrandliveEvent, Dictionary, PrivacyPolicy, TermsConditions } from '../../../types/working-model';
import { getStorageItem, setStorageItem, setStorageObject } from '../../../utils/local-storage';
import {
	COMPLETE_PROFILE,
	GET_USER_SESSION,
	REGISTER,
	SET_BL_USER,
	SET_REGISTRATION_STEP,
	SET_USER_SESSION,
	TOGGLE_REGISTRATION_MODAL,
	SET_BL_PROFILE_TOKEN,
	SET_COMPLETED_SURVEY_QUESTIONS,
	SET_COMPLETED_MULTI_QUESTIONS,
	SET_COMPLETED_TEXT_QUESTIONS,
	RESET_SURVEY,
	GET_LIVE_TERMS_CONDITIONS,
	GET_LIVE_PRIVACY_POLICY,
	UPDATE_EVENT_BUNDLE,
	CREATE_STRIPE_INTENT,
	FINISHED_STRIPE_PAYMENT,
	LOAD_PAGE,
	LOAD_EVENT_BUNDLE,
	GET_TICKET_INFO,
	RE_REGISTER,
} from '../../actions/event/event-actions';

export interface LiveEventState {
	eventBundle: BrandliveEvent | null;
	playbackUrls: Dictionary;
	loadingEventBundle: boolean;
	eventBundleError: string;
	blProfileUserToken: string | undefined;
	blProfileUser: BlProfile | null;
	registrationId: string | null;
	registering: boolean;
	validating: boolean;
	validationFailure: boolean;
	registrationError: string;
	validSessions: string[];
	mustVerify: boolean;
	shouldAwaitValidation: boolean;
	validRegistration: boolean;
	validationChecked: boolean;
	userSession: string;
	gettingUserSession: boolean;
	registrationStep: number;
	finishedRegistering: boolean;
	completingProfile: boolean;
	showRegistrationModal: string | null;
	userSesssion: string | null;
	getUserSessionError: string | null;
	surveyQuestionsCompleted: { question: number, option: number }[];
	multiQuestionsCompleted: { question: number, options: { [key: number]: number } }[];
	surveyTextCompleted: { question: number, answer: string }[];
	termsConditions: TermsConditions | null;
	privacyPolicy: PrivacyPolicy | null;
	stripeIntent: any;
	stripeIntentError: any;
	stripePaymentError: any;
	_404: boolean;
	verificationCode: string | null;
	shouldAwaitVerification: boolean;
	verifyingUser: boolean;
	userVerified: boolean;
	verifyingFailed: boolean;
	shouldRedirectToRegistration: boolean;
	numberOfTicketsPurchased: { [key: string]: number } | null;
	paymentRequired: boolean;
	paidSessions?: string[];
}

// enum RegFailureReasons {
// 	passcode = 'invalid-passcode',
// 	domain = 'invalid-email-domain',
// 	missingRequired = 'missing-required-field',
// 	missingPasscodeLists = 'no-matching-passcode-lists',
// 	notFoundInPasscodeLists = 'not-found-passcode-lists'
// }

const storedToken = getStorageItem('bl-profile');
const queryParams = new URLSearchParams(window.location.search);
const verification = queryParams.get('code');

const initialState: LiveEventState = {
	eventBundle: null,
	playbackUrls: {},
	loadingEventBundle: false,
	eventBundleError: "",
	blProfileUserToken: storedToken,
	blProfileUser: storedToken ? jwtDecode(storedToken) : null,
	registrationId: null,
	registering: false,
	registrationError: '',
	validating: false,
	shouldAwaitValidation: true,
	validationFailure: false,
	validSessions: [],
	mustVerify: false,
	validRegistration: false,
	validationChecked: false,
	userSession: '',
	gettingUserSession: false,
	registrationStep: 0,
	finishedRegistering: false,
	completingProfile: false,
	showRegistrationModal: null,
	userSesssion: null,
	getUserSessionError: null,
	surveyQuestionsCompleted: [],
	multiQuestionsCompleted: [],
	surveyTextCompleted: [],
	termsConditions: null,
	privacyPolicy: null,
	stripeIntent: null,
	stripeIntentError: null,
	stripePaymentError: null,
	_404: false,
	verificationCode: verification,
	verifyingUser: false,
	userVerified: false,
	verifyingFailed: false,
	shouldAwaitVerification: !!verification,
	shouldRedirectToRegistration: false,
	numberOfTicketsPurchased: null,
	paymentRequired: false
};

export default function LiveEventReducer(state = initialState, action: Action): LiveEventState {
	const { type } = action;
	switch (type) {
		case LOAD_PAGE: {
			return handle(state, action, {
				finish: state => ({ ...state, loadingEventBundle: false }),
				success: state => ({ ...state, ...action.payload })
			});
		}
		case LOAD_EVENT_BUNDLE: {
			return handle(state, action, {
				start: state => ({ ...state, loadingEventBundle: true }),
				success: state => ({ ...state, ...action.payload })
			});
		}
		case GET_USER_SESSION: {
			return handle(state, action, {
				success: state => {
					setStorageItem(`ses.${action.payload.session}`, action.payload.uuid, 5);
					return ({ ...state, userSesssion: action.payload.uuid, getUserSessionError: action.payload.getUserSessionError });
				},
				start: state => ({ ...state, gettingUserSession: true, getUserSessionError: null }),
				finish: state => ({ ...state, gettingUserSession: false })
			});
		}
		case SET_USER_SESSION: {
			return {
				...state,
				userSession: action.payload
			};
		}
		case SET_BL_USER: {
			return {
				...state,
				blProfileUser: action.payload,
			};
		}
		case CREATE_STRIPE_INTENT: {
			return handle(state, action, {
				success: state => {
					if ("message" in action.payload) {
						return {
							...state,
							stripeIntentError: action.payload.message,
						};
					}

					return {
						...state,
						stripeIntent: action.payload,
					};
				},
				failure: state => {
					return {
						...state,
						stripeIntentError: action.payload.message,
					};
				}
			});
		}
		case FINISHED_STRIPE_PAYMENT: {
			const stepsOn = state.eventBundle!.registration_steps!.filter(step => step.isOn);
			let finished = false;

			if (action.payload.status == 'succeeded') {
				if (state.registrationStep + 1 === stepsOn.length) {
					finished = true;
				}

				//set returning registration step
				setStorageItem(
					`registration-step-${state.eventBundle!.uuid}`,
					String(state.registrationStep + 1),
					48
				);

				return {
					...state,
					registrationStep: state.registrationStep + 1,
					stripePaymentError: null,
					finishedRegistering: finished,
					paymentRequired: false
				};
			}

			return {
				...state,
				stripePaymentError: action.payload.status
			};
		}
		case REGISTER: {
			return handle(state, action, {
				start: state => ({
					...state,
					registering: true
				}),
				success: state => {
					if (!state.eventBundle) return state;

					if ('failed' in action.payload) {
						return {
							...state,
							registrationError: action.payload.failed
						};
					}

					if ('error' in action.payload) {
						return {
							...state,
							registrationError: action.payload.error
						};
					} else {
						setStorageItem(`reg.${state.eventBundle!.uuid}`, action.payload.registration);
						setStorageObject(`allowed.${state.eventBundle!.uuid}`, action.payload.validSessions);

						//do not store a dead token
						if (action.payload.token) {
							setStorageItem('bl-profile', action.payload.token);

							// because chat requires potential non registered users
							// we use a different token for that, but if the event is registered,
							// we should use the registered token
							setStorageItem('Bl-Profile-Token', action.payload.token);
						}

						const mustVerify = action.payload.mustVerify;

						if (mustVerify) {
							setStorageItem(`should-cont-reg-${state.eventBundle!.uuid}`, "true", 24);
						}

						//set returning registration step
						setStorageItem(
							`registration-step-${state.eventBundle!.uuid}`,
							String(state.registrationStep + 1),
							48
						);

						const stepsOn = state.eventBundle.registration_steps!.filter(step => step.isOn);

						let finished = false;

						if (state.registrationStep + 1 === stepsOn.length) {
							finished = true;
						}

						//do not try to delete a dead token
						const user = action.payload.token ? jwtDecode(action.payload.token) as BlProfile : null;

						return {
							...state,
							registrationId: action.payload.registration,
							blProfileUserToken: action.payload.token ? action.payload.token : null,
							blProfileUser: user,
							mustVerify: action.payload.mustVerify,
							validRegistration: true,
							eventBundle: {
								...state.eventBundle,
								sessions: action.payload.secureEvent.sessions
							},
							registrationError: '',
							finishedRegistering: finished,
							registrationStep: state.registrationStep + 1,
							playbackUrls: action.payload.playbackUrls ?? {},
							validSessions: action.payload.validSessions,
							shouldRedirectToRegistration: false
						};
					}
				},
				failure: state => ({
					...state,
					registrationError: 'Unable to register, please try again.'
				}),
				finish: state => ({
					...state,
					registering: false,
					validationChecked: true
				})
			});
		}

		case RE_REGISTER: {
			return handle(state, action, {
				start: state => ({ ...state, registering: true }),
				finish: state => ({ ...state, registering: false, mustVerify: true })
			});
		}

		case COMPLETE_PROFILE: {
			return handle(state, action, {
				start: state => ({ ...state, completingProfile: true }),
				finish: state => ({ ...state, completingProfile: false }),
				success: state => {
					if (!state.eventBundle) return state;

					setStorageItem('bl-profile', action.payload.token);
					setStorageItem('Bl-Profile-Token', action.payload.token);

					const stepsOn = state.eventBundle.registration_steps!.filter(step => step.isOn);

					let finished = false;

					if (state.registrationStep + 1 === stepsOn.length) {
						finished = true;
					}

					return {
						...state,
						blProfileUserToken: action.payload.token,
						blProfileUser: jwtDecode(action.payload.token),
						registrationStep: state.registrationStep + 1,
						finishedRegistering: finished
					};
				}
			});
		}

		case SET_REGISTRATION_STEP: {
			return ({ ...state, registrationStep: action.payload });
		}
		case TOGGLE_REGISTRATION_MODAL: {
			return {
				...state,
				showRegistrationModal: action.payload,
			};
		}
		case SET_BL_PROFILE_TOKEN: {
			return {
				...state,
				blProfileUserToken: action.payload,
			};
		}
		case SET_COMPLETED_SURVEY_QUESTIONS: {
			// if store already containes answers for the given question, replace with new answers
			// else, add answers to array
			const found = state.surveyQuestionsCompleted.some(option => option.question === action.payload.question);
			if (found) {
				const array = state.surveyQuestionsCompleted.filter(option => option.question !== action.payload.question);
				return {
					...state,
					surveyQuestionsCompleted: [array, action.payload]
				};
			} else {
				return {
					...state,
					surveyQuestionsCompleted: [...state.surveyQuestionsCompleted, action.payload]
				};
			}
		}
		case SET_COMPLETED_MULTI_QUESTIONS: {
			const found = state.multiQuestionsCompleted.some(option => option.question === action.payload.question);
			if (found) {
				const array = state.multiQuestionsCompleted.filter(option => option.question !== action.payload.question);
				return {
					...state,
					multiQuestionsCompleted: [array, action.payload]
				};
			} else {
				return {
					...state,
					multiQuestionsCompleted: [...state.multiQuestionsCompleted, action.payload]
				};
			}
		}
		case SET_COMPLETED_TEXT_QUESTIONS: {
			const found = state.surveyTextCompleted.some(option => option.question === action.payload.question);
			if (found) {
				const array = state.surveyTextCompleted.filter(answer => answer.question !== action.payload.question);
				return {
					...state,
					surveyTextCompleted: [array, action.payload]
				};
			} else {
				return {
					...state,
					surveyTextCompleted: [...state.surveyTextCompleted, action.payload]
				};
			}
		}
		case RESET_SURVEY: {
			if (action.payload) {
				const radioArray = state.surveyQuestionsCompleted.filter(option => !action.payload.includes(option.question));
				const multiArray = state.multiQuestionsCompleted.filter(option => !action.payload.includes(option.question));
				const textArray = state.surveyTextCompleted.filter(option => !action.payload.includes(option.question));
				return {
					...state,
					surveyQuestionsCompleted: radioArray,
					multiQuestionsCompleted: multiArray,
					surveyTextCompleted: textArray
				};
			} else return state;
		}
		case GET_LIVE_TERMS_CONDITIONS: {
			return handle(state, action, {
				success: state => {
					return { ...state, termsConditions: action.payload };
				}
			});
		}
		case GET_LIVE_PRIVACY_POLICY: {
			return handle(state, action, {
				success: state => {
					return { ...state, privacyPolicy: action.payload };
				}
			});
		}
		case UPDATE_EVENT_BUNDLE: {
			return {
				...state,
				eventBundle: action.payload
			};
		}
		case GET_TICKET_INFO: {
			return handle(state, action, {
				success: state => {
					return {
						...state,
						numberOfTicketsPurchased: action.payload,
					};
				},
				failure: state => {
					return {
						...state,
					};
				}
			});
		}
		default: return state;
	}
}